import React, { useCallback, useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Tooltip from "@material-ui/core/Tooltip";
import Chip from "@material-ui/core/Chip";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Switch,
  TablePagination,
  Box,
  Typography,
} from "@material-ui/core";
import { isArray } from "lodash";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_DATA") {
    const data = action.payload;
    const newData = [];
    if (isArray(data)) {
      data.forEach((item) => {
        const index = state.findIndex((u) => u.id === item.id);
        if (index !== -1) {
          state[index] = item;
        } else {
          newData.push(item);
        }
      });
    }
    return [...state, ...newData];
  }
  if (action.type === "DELETE_DATA") {
    const id = action.payload;
    const index = state.findIndex((u) => u.id === id);
    if (index !== -1) {
      state.splice(index, 1);
    }
    return [...state];
  }
  if (action.type === "UPDATE_DATA") {
    const item = action.payload;
    const index = state.findIndex((u) => u.id === item.id);
    if (index !== -1) {
      state[index] = item;
      return [...state];
    }
    return [item, ...state];
  }
  if (action.type === "RESET") {
    return [];
  }
  return state;
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  headerCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: "#f5f5f5",
    borderRadius: 8,
  },
  tableHeader: {
    fontWeight: "bold",
    backgroundColor: theme.palette.grey[100],
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(6),
    color: theme.palette.text.secondary,
  },
  variableChip: {
    margin: theme.spacing(0.25),
    fontSize: "0.7rem",
    height: 20,
  },
  backButton: {
    marginRight: theme.spacing(2),
  },
}));

const ContactListProItems = ({ official = false }) => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { contactListProId, officialContactListId } = useParams();
  const listId = official ? officialContactListId : contactListProId;
  const basePath = official ? "/official-contact-lists" : "/contact-lists-pro";
  const backPath = official ? "/official-campaigns?tab=contacts" : "/campaigns-pro";
  const { user, socket } = useContext(AuthContext);
  const contactListFromNavigation =
    official && location.state?.contactList?.id === Number(listId)
      ? location.state.contactList
      : null;

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [searchParam, setSearchParam] = useState("");
  const [contactList, setContactList] = useState(contactListFromNavigation);

  const [contacts, dispatch] = useReducer(reducer, []);
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    number: "",
    var1: "",
    var2: "",
    var3: "",
    var4: "",
    var5: "",
    var6: "",
    tags: "",
    optedIn: true,
  });

  // Carregar dados da lista
  useEffect(() => {
    let isMounted = true;

    const fetchContactList = async () => {
      if (official) {
        if (contactListFromNavigation && isMounted) {
          setContactList(contactListFromNavigation);
        }

        try {
          const { data } = await api.get("/official-contact-lists", {
            params: {
              pageSize: 100
            }
          });

          const records = Array.isArray(data) ? data : data?.records;
          const fallbackList = Array.isArray(records)
            ? records.find(item => Number(item.id) === Number(listId))
            : null;

          if (fallbackList && isMounted) {
            setContactList(fallbackList);
            return;
          }
        } catch (fallbackError) {
          console.error("[ContactListProItems] Falha ao carregar lista Meta pela listagem:", fallbackError);
        }

        if (isMounted) {
          setContactList(current => current || {
            id: Number(listId),
            name: `Lista ${listId}`
          });
        }
        return;
      }

      try {
        const { data } = await api.get(`${basePath}/${listId}`);
        if (isMounted) {
          setContactList(data);
        }
      } catch (err) {
        toastError(err);
        history.push(backPath);
      }
    };

    fetchContactList();

    return () => {
      isMounted = false;
    };
  }, [listId, basePath, backPath, history, official, contactListFromNavigation]);

  // Reset ao mudar filtros
  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, pageSize]);

  // Carregar contatos
  const fetchContacts = useCallback(async () => {
    try {
      const { data } = await api.get(`${basePath}/${listId}/items`, {
        params: {
          searchParam,
          pageNumber,
          pageSize,
        },
      });

      if (pageNumber === 1) {
        dispatch({ type: "RESET" });
      }
      dispatch({ type: "LOAD_DATA", payload: data.records });
      setTotalCount(data.count || 0);
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  }, [basePath, listId, pageNumber, pageSize, searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [fetchContacts]);

  // Socket listeners
  useEffect(() => {
    const companyId = user.companyId;

    const onContactListItemPro = (data) => {
      if (data.action === "delete") {
        dispatch({ type: "DELETE_DATA", payload: +data.id });
      }
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_DATA", payload: data.record });
      }
    };

    socket.on(`company-${companyId}-ContactListItemPro`, onContactListItemPro);

    return () => {
      socket.off(`company-${companyId}-ContactListItemPro`, onContactListItemPro);
    };
  }, [socket, user.companyId]);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handlePageChange = (event, newPage) => {
    setPageNumber(newPage + 1);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setPageNumber(1);
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await api.delete(`${basePath}/${listId}/items/${contactId}`);
      toast.success("Contato excluído!");
      dispatch({ type: "DELETE_DATA", payload: contactId });
      setTotalCount((prev) => prev - 1);
    } catch (err) {
      toastError(err);
    }
    setDeletingContact(null);
  };

  const handleOpenEditModal = (contact) => {
    setEditingContact(contact);
    setEditForm({
      name: contact.name || "",
      number: contact.number || "",
      var1: contact.var1 || "",
      var2: contact.var2 || "",
      var3: contact.var3 || "",
      var4: contact.var4 || "",
      var5: contact.var5 || "",
      var6: contact.var6 || "",
      tags: contact.tags || "",
      optedIn: contact.optedIn !== false,
    });
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditingContact(null);
    setEditModalOpen(false);
  };

  const handleEditFormChange = (field) => (event) => {
    const value = field === "optedIn" ? event.target.checked : event.target.value;
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveEditContact = async () => {
    if (!editingContact) return;
    try {
      const { data } = await api.put(
        `${basePath}/${listId}/items/${editingContact.id}`,
        editForm
      );
      dispatch({ type: "UPDATE_DATA", payload: data });
      toast.success("Contato atualizado!");
      handleCloseEditModal();
    } catch (err) {
      toastError(err);
    }
  };

  const renderVariables = (contact) => {
    const vars = [];
    if (contact.var1) vars.push({ key: "var1", value: contact.var1 });
    if (contact.var2) vars.push({ key: "var2", value: contact.var2 });
    if (contact.var3) vars.push({ key: "var3", value: contact.var3 });
    if (contact.var4) vars.push({ key: "var4", value: contact.var4 });
    if (contact.var5) vars.push({ key: "var5", value: contact.var5 });
    if (contact.var6) vars.push({ key: "var6", value: contact.var6 });

    if (vars.length === 0) return <Typography variant="caption" color="textSecondary">—</Typography>;

    return (
      <Box>
        {vars.map((v) => (
          <Tooltip key={v.key} title={`${v.key}: ${v.value}`}>
            <Chip
              label={`${v.key}: ${v.value.substring(0, 15)}${v.value.length > 15 ? "..." : ""}`}
              size="small"
              className={classes.variableChip}
              variant="outlined"
            />
          </Tooltip>
        ))}
      </Box>
    );
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={deletingContact && `Excluir contato "${deletingContact.name || deletingContact.number}"?`}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteContact(deletingContact.id)}
      >
        Esta ação não pode ser desfeita.
      </ConfirmationModal>

      <Dialog open={editModalOpen} onClose={handleCloseEditModal} fullWidth maxWidth="sm">
        <DialogTitle>Editar contato da lista</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Nome" value={editForm.name} onChange={handleEditFormChange("name")} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Telefone" value={editForm.number} onChange={handleEditFormChange("number")} />
            </Grid>
            {[1, 2, 3, 4, 5, 6].map((number) => (
              <Grid item xs={12} md={6} key={number}>
                <TextField
                  fullWidth
                  label={`Variavel ${number}`}
                  value={editForm[`var${number}`]}
                  onChange={handleEditFormChange(`var${number}`)}
                />
              </Grid>
            ))}
            <Grid item xs={12}>
              <TextField fullWidth label="Tags" value={editForm.tags} onChange={handleEditFormChange("tags")} />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch color="primary" checked={editForm.optedIn} onChange={handleEditFormChange("optedIn")} />}
                label="Contato autorizou receber mensagens"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditModal} color="secondary" variant="outlined">
            Cancelar
          </Button>
          <Button onClick={handleSaveEditContact} color="primary" variant="contained">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <MainHeader>
        <Box display="flex" alignItems="center">
          <IconButton
            className={classes.backButton}
            onClick={() => history.push(backPath)}
          >
            <ArrowBackIcon />
          </IconButton>
          <Title>
            Contatos {official ? "Meta" : "PRO"}: {contactList?.name || "Carregando..."}
          </Title>
        </Box>
      </MainHeader>

      <Paper className={classes.headerCard}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              placeholder="Buscar por nome ou número..."
              type="search"
              value={searchParam}
              onChange={handleSearch}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "gray" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box display="flex" gap={2} justifyContent="flex-end">
              <Chip
                label={`Total: ${totalCount} contatos`}
                color="primary"
              />
              {contactList && (
                <>
                  <Chip
                    label={`Importados: ${contactList.totalImported || 0}`}
                    style={{ backgroundColor: "#4caf50", color: "white" }}
                  />
                  <Chip
                    label={`Duplicados: ${contactList.totalDuplicates || 0}`}
                    style={{ backgroundColor: "#ff9800", color: "white" }}
                  />
                  <Chip
                    label={`Inválidos: ${contactList.totalInvalid || 0}`}
                    style={{ backgroundColor: "#f44336", color: "white" }}
                  />
                </>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeader}>Nome</TableCell>
              <TableCell className={classes.tableHeader}>Número</TableCell>
              <TableCell className={classes.tableHeader}>Variáveis</TableCell>
              <TableCell className={classes.tableHeader}>Tags</TableCell>
              <TableCell className={classes.tableHeader} align="center">Opt-in</TableCell>
              <TableCell className={classes.tableHeader} align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contacts.map((contact) => (
              <TableRow key={contact.id}>
                <TableCell>{contact.name || <em style={{ color: "#999" }}>Sem nome</em>}</TableCell>
                <TableCell>
                  <Typography variant="body2" style={{ fontFamily: "monospace" }}>
                    {contact.number}
                  </Typography>
                </TableCell>
                <TableCell>{renderVariables(contact)}</TableCell>
                <TableCell>
                  {contact.tags ? (
                    <Chip label={contact.tags} size="small" />
                  ) : (
                    <Typography variant="caption" color="textSecondary">—</Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={contact.optedIn ? "Sim" : "Não"}
                    size="small"
                    color={contact.optedIn ? "primary" : "default"}
                  />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Editar contato">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEditModal(contact)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Excluir contato">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setConfirmModalOpen(true);
                        setDeletingContact(contact);
                      }}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {loading && <TableRowSkeleton columns={6} />}
            {!loading && contacts.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className={classes.emptyState}>
                  <Typography variant="body1">
                    Nenhum contato encontrado.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <Box display="flex" justifyContent="flex-end" p={2}>
          <TablePagination
            component="div"
            count={totalCount}
            page={pageNumber - 1}
            onPageChange={handlePageChange}
            rowsPerPage={pageSize}
            onRowsPerPageChange={handlePageSizeChange}
            rowsPerPageOptions={[10, 20, 50, 100]}
            labelRowsPerPage="Por página:"
          />
        </Box>
      </Paper>
    </MainContainer>
  );
};

export default ContactListProItems;
